import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectDataLoaded } from '../../settings/stores/company-settings-slice-handler';
import ContactsSelector from './contacts-selector';
import TransferListArrows from '../../shared/components/transfer-list-arrows';
import {
    resetContactsSelectorData,
    sortGroupsIdsList,
    sortMsisdnsList,
    updateContactsSelectorData,
} from '../stores/contacts-slice-handler';
import { contactsRecordsOnPage, createEmptyContactsIdsList } from '../../../utils/contact-util';

/**
 * Creates a transfer list of contacts.
 * @param {string} listId An unique identifier which of the list.
 * @param {ContactsIdsList} selectedContactsIds The ids of the selected contacts, msisdns and groups ids.
 * @param {ContactsIdsList} lockedContactsIds The ids of the selected contacts which cannot be removed from the assigned box. Ex: the creator of a group.
 * @param {(data: ContactsIdsList) => void} onTransfer A callback to be executed when the items have been moved from one side to another. The callback will receive as parameter the current selection.
 * @param {boolean} onlyUsers Display only users tab.
 * @param {number} recordsOnPage The number of records on page to be used in the box which is loading available contacts. (optional)
 * @return {JSX.Element}
 * @constructor
 */

export default function ContactsTransferList({
    listId,
    selectedContactsIds,
    lockedContactsIds,
    onTransfer,
    onlyUsers,
    recordsOnPage,
}) {
    const uuidContactsSelectorAvailableContacts = `contacts-transfer-list-${listId}-available-contacts`;
    const uuidContactsSelectorAssignedContacts = `contacts-transfer-list-${listId}-assigned-contacts`;

    const [assignedUsers, setAssignedUsers] = useState([]);
    const [assignedGroups, setAssignedGroups] = useState([]);
    const [selectedAvailableUsers, setSelectedAvailableUsers] = useState([]);
    const [selectedAvailableGroups, setSelectedAvailableGroups] = useState([]);
    const [selectedAssignedUsers, setSelectedAssignedUsers] = useState([]);
    const [selectedAssignedGroups, setSelectedAssignedGroups] = useState([]);

    if (typeof selectedContactsIds === 'undefined') {
        selectedContactsIds = createEmptyContactsIdsList();
    }

    useEffect(() => {
        if (typeof selectedContactsIds !== 'undefined') {
            if (typeof selectedContactsIds.msisdns !== 'undefined') {
                setAssignedUsers(selectedContactsIds.msisdns);
            }

            if (typeof selectedContactsIds.groupsIds !== 'undefined') {
                setAssignedGroups(selectedContactsIds.groupsIds);
            }
        }
    }, [selectedContactsIds]);

    const handleTransfer = (data) => {
        if (typeof onTransfer === 'function') {
            onTransfer(data);
        }
    };

    return (
        <div className="contacts-transfer-list">
            <div className="selector-container">
                <ContactsSelector
                    contactsSelectorUuid={uuidContactsSelectorAvailableContacts}
                    options={{
                        recordsOnPage: recordsOnPage || contactsRecordsOnPage(),
                        displayOnlyUsers: !!onlyUsers,
                        multipleSelectionOpen: true,
                        searchOnlyMode: true,
                        lockSelectedContacts: true,
                        users: {
                            selectedMsisdns: assignedUsers,
                        },
                        groups: {
                            selectedGroupsIds: assignedGroups,
                        },
                        onSelectionChanged: (selection) => {
                            if (typeof selection.msisdns !== 'undefined') {
                                setSelectedAvailableUsers(
                                    selection.msisdns.filter((msisdn) => !assignedUsers.includes(msisdn)),
                                );
                            }

                            if (typeof selection.groupsIds !== 'undefined') {
                                setSelectedAvailableGroups(
                                    selection.groupsIds.filter((msisdn) => !assignedGroups.includes(msisdn)),
                                );
                            }
                        },
                        onTabChanged: (tab) => {
                            // switch the other box to this tab
                            updateContactsSelectorData(uuidContactsSelectorAssignedContacts, { currentTab: tab });
                        },
                    }}
                />
            </div>
            <TransferListArrows
                rightToLeftEnabled={selectedAssignedUsers.length > 0 || selectedAssignedGroups.length > 0}
                rightToLeftCallback={() => {
                    /**
                     * Must use this because the assignments list is not computed until the next render.
                     * @type {ContactsIdsList}
                     */
                    const newAssignmentData = {
                        msisdns: assignedUsers.filter((msisdn) => !selectedAssignedUsers.includes(msisdn)),
                        groupsIds: assignedGroups.filter((groupId) => !selectedAssignedGroups.includes(groupId)),
                    };
                    setAssignedUsers(newAssignmentData.msisdns);
                    setAssignedGroups(newAssignmentData.groupsIds);
                    resetContactsSelectorData(uuidContactsSelectorAssignedContacts);
                    setSelectedAssignedUsers([]);
                    setSelectedAssignedGroups([]);
                    handleTransfer(newAssignmentData);
                }}
                leftToRightEnabled={selectedAvailableUsers.length > 0 || selectedAvailableGroups.length > 0}
                leftToRightCallback={() => {
                    /**
                     * Must use this because the assignments list is not computed until the next render.
                     * @type {ContactsIdsList}
                     */
                    const newAssignmentData = {
                        msisdns: sortMsisdnsList(selectedAvailableUsers.concat(assignedUsers)),
                        groupsIds: sortGroupsIdsList(selectedAvailableGroups.concat(assignedGroups)),
                    };
                    setAssignedUsers(newAssignmentData.msisdns);
                    setAssignedGroups(newAssignmentData.groupsIds);
                    resetContactsSelectorData(uuidContactsSelectorAvailableContacts);
                    setSelectedAvailableUsers([]);
                    setSelectedAvailableGroups([]);
                    handleTransfer(newAssignmentData);
                }}
            />
            <div className="selector-container">
                <ContactsSelector
                    contactsSelectorUuid={uuidContactsSelectorAssignedContacts}
                    options={{
                        multipleSelectionOpen: true,
                        displayOnlyUsers: !!onlyUsers,
                        users: {
                            availableMsisdns: assignedUsers,
                        },
                        groups: {
                            availableGroupsIds: assignedGroups,
                        },
                        onSelectionChanged: (selection) => {
                            setSelectedAssignedUsers(selection.msisdns);
                            setSelectedAssignedGroups(selection.groupsIds);
                        },
                        onTabChanged: (tab) => {
                            // switch the other box to this tab
                            updateContactsSelectorData(uuidContactsSelectorAvailableContacts, { currentTab: tab });
                        },
                    }}
                />
            </div>
        </div>
    );
}
